import { useState, useMemo } from "react";
import { startCase } from "lodash";

import { Checkbox } from "primereact/checkbox";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Chip } from "primereact/chip";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

import {
  languageDisplayNames,
  technologyDisplayNames,
  difficultyDisplayNames,
  LESSONS_RELEASE_OPTIONS,
  LESSONS_CONTENT_TYPE_OPTIONS,
} from "sj-ui-toolkit/src/constants";

import type { MappedCweTag } from "@/hooks/useCweTags";
import type { TagsData } from "@/api/resources";

import S from "./LessonFilterPanel.module.scss";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getLanguageDisplayName = (languageValue: string) => {
  if (!languageValue) return "";
  if (languageValue === "c++") return "C++";
  if (languageValue === "c#") return "C#";
  return (
    (languageDisplayNames as Record<string, string>)[languageValue] ??
    startCase(languageValue.replace(/_/g, " "))
  );
};

const getTechnologyDisplayName = (technologyValue: string) => {
  if (!technologyValue) return "";
  return (
    (technologyDisplayNames as Record<string, string>)[technologyValue] ??
    startCase(technologyValue.replace(/_/g, " "))
  );
};

const DIFFICULTY_OPTIONS = Object.keys(difficultyDisplayNames as Record<string, string>).map(
  value => ({
    value,
    label: (difficultyDisplayNames as Record<string, string>)[value]!,
  })
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FilterState {
  focus: string[];
  difficulty: string[];
  release: string[];
  contentType: string[];
  languages: string[];
  technologies: string[];
  cwe: number[];
}

interface LessonFilterPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  tagsData: TagsData;
  cweTags: MappedCweTag[];
  filters: FilterState;
  onFilterChange: (category: keyof FilterState, values: (string | number)[]) => void;
  onClearFilters: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const LessonFilterPanel = ({
  isOpen,
  onToggle,
  tagsData,
  cweTags,
  filters,
  onFilterChange,
  onClearFilters,
}: LessonFilterPanelProps) => {
  const [languageSearch, setLanguageSearch] = useState("");
  const [technologySearch, setTechnologySearch] = useState("");
  const [cweSearch, setCweSearch] = useState("");

  const tags = useMemo(() => {
    const languages =
      tagsData.tagsCategories
        ?.find(tc => tc.name?.toLowerCase() === "language")
        ?.tags?.map(tag => tag.name) ?? [];
    const technologies =
      tagsData.tagsCategories
        ?.find(tc => tc.name?.toLowerCase() === "technology")
        ?.tags?.map(tag => tag.name) ?? [];
    return { languages: [...languages].sort(), technologies: [...technologies].sort() };
  }, [tagsData]);

  const filteredLanguages = useMemo(() => {
    if (!languageSearch) return tags.languages;
    const s = languageSearch.toLowerCase();
    return tags.languages.filter(lang => lang.toLowerCase().includes(s));
  }, [tags.languages, languageSearch]);

  const filteredTechnologies = useMemo(() => {
    if (!technologySearch) return tags.technologies;
    const s = technologySearch.toLowerCase();
    return tags.technologies.filter(tech => tech.toLowerCase().includes(s));
  }, [tags.technologies, technologySearch]);

  const filteredCwe = useMemo(() => {
    if (!cweTags.length) return [];
    if (!cweSearch) return cweTags;
    const s = cweSearch.toLowerCase();
    return cweTags.filter(
      cwe =>
        cwe.value.toString().includes(s) ||
        cwe.name.toLowerCase().includes(s) ||
        (cwe.description?.toLowerCase().includes(s) ?? false)
    );
  }, [cweTags, cweSearch]);

  const handleCheckboxChange = (category: keyof FilterState, value: string | number) => {
    const current = filters[category] as (string | number)[];
    const newValues = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onFilterChange(category, newValues);
  };

  const isChecked = (category: keyof FilterState, value: string | number) =>
    (filters[category] as (string | number)[]).includes(value);

  interface ActiveFilterEntry {
    key: string;
    label: string;
    category: keyof FilterState;
    value?: string | number;
    isGroup?: boolean;
  }

  const getActiveFiltersInfo = () => {
    const activeFilters: ActiveFilterEntry[] = [];
    let totalCount = 0;

    (["difficulty", "release", "contentType"] as (keyof FilterState)[]).forEach(category => {
      const values = filters[category] as string[];
      values.forEach(value => {
        totalCount++;
        if (activeFilters.length < 5) {
          let label = value;
          if (category === "difficulty") {
            label = DIFFICULTY_OPTIONS.find(o => o.value === value)?.label ?? value;
          }
          if (category === "contentType") {
            label =
              (LESSONS_CONTENT_TYPE_OPTIONS as { value: string; label: string }[]).find(
                o => o.value === value
              )?.label ?? value;
          }
          if (category === "release") {
            label =
              (LESSONS_RELEASE_OPTIONS as { value: string; label: string }[]).find(
                o => o.value === value
              )?.label ?? value;
          }
          activeFilters.push({ key: `${category}-${value}`, label, category, value });
        }
      });
    });

    const selectedLanguages = filters.languages;
    if (selectedLanguages.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedLanguages.length === 1) {
          activeFilters.push({
            key: `languages-${selectedLanguages[0]}`,
            label: getLanguageDisplayName(selectedLanguages[0]!),
            category: "languages",
            value: selectedLanguages[0],
          });
        } else {
          activeFilters.push({
            key: "languages-group",
            label: `${selectedLanguages.length} Languages`,
            category: "languages",
            isGroup: true,
          });
        }
      }
    }

    const selectedTechnologies = filters.technologies;
    if (selectedTechnologies.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedTechnologies.length === 1) {
          activeFilters.push({
            key: `technologies-${selectedTechnologies[0]}`,
            label: getTechnologyDisplayName(selectedTechnologies[0]!),
            category: "technologies",
            value: selectedTechnologies[0],
          });
        } else {
          activeFilters.push({
            key: "technologies-group",
            label: `${selectedTechnologies.length} Technologies`,
            category: "technologies",
            isGroup: true,
          });
        }
      }
    }

    const selectedCwe = filters.cwe;
    if (selectedCwe.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedCwe.length === 1) {
          const cweData = tagsData.cweTags?.find(cwe => cwe.cweTag?.id === selectedCwe[0]);
          const cweName = cweData?.cweTag?.name ?? `CWE-${selectedCwe[0]}`;
          activeFilters.push({
            key: `cwe-${selectedCwe[0]}`,
            label: cweName,
            category: "cwe",
            value: selectedCwe[0],
          });
        } else {
          activeFilters.push({
            key: "cwe-group",
            label: `${selectedCwe.length} CWEs`,
            category: "cwe",
            isGroup: true,
          });
        }
      }
    }

    return { activeFilters, totalCount, remainingCount: totalCount - activeFilters.length };
  };

  const { activeFilters, totalCount, remainingCount } = getActiveFiltersInfo();

  const handleRemoveFilter = (filter: ActiveFilterEntry) => {
    if (filter.isGroup) {
      onFilterChange(filter.category, []);
    } else {
      handleCheckboxChange(filter.category, filter.value!);
    }
  };

  return (
    <div className={S.filterPanel}>
      <div className={S.filterHeader}>
        <button type="button" className={S.filterToggle} onClick={onToggle} aria-expanded={isOpen}>
          <i className={`pi ${isOpen ? "pi-chevron-up" : "pi-chevron-down"}`} />
          <span className={S.filterTitle}>Advanced Filters</span>
        </button>

        {totalCount > 0 && (
          <div className={S.filterPreview}>
            {activeFilters.map(filter => (
              <Chip
                key={filter.key}
                label={filter.label}
                removable
                onRemove={() => {
                  handleRemoveFilter(filter);
                  return true;
                }}
                className={S.previewChip}
              />
            ))}
            {remainingCount > 0 && <span className={S.moreFilters}>+{remainingCount} more</span>}
            <Button label="Clear filters" link className={S.clearButton} onClick={onClearFilters} />
          </div>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-900 ease-in-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className={S.filterContent}>
            <div className={S.filterGrid}>
              {tags.languages.length > 0 && (
                <div className={S.filterSection}>
                  <h4 className={S.sectionTitle}>Language</h4>
                  <div className={S.searchInput}>
                    <IconField iconPosition="left">
                      <InputIcon className="pi pi-search" />
                      <InputText
                        value={languageSearch}
                        onChange={e => setLanguageSearch(e.target.value)}
                        placeholder="Search languages"
                        className={S.filterSearch}
                        aria-label="Search languages"
                      />
                    </IconField>
                  </div>
                  <div className={S.checkboxList}>
                    {filteredLanguages.map(lang => (
                      <div key={lang} className={S.checkboxItem}>
                        <Checkbox
                          inputId={`language-${lang}`}
                          checked={isChecked("languages", lang)}
                          onChange={() => handleCheckboxChange("languages", lang)}
                        />
                        <label htmlFor={`language-${lang}`} className={S.checkboxLabel}>
                          {getLanguageDisplayName(lang)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tags.technologies.length > 0 && (
                <div className={S.filterSection}>
                  <h4 className={S.sectionTitle}>Technology</h4>
                  <div className={S.searchInput}>
                    <IconField iconPosition="left">
                      <InputIcon className="pi pi-search" />
                      <InputText
                        value={technologySearch}
                        onChange={e => setTechnologySearch(e.target.value)}
                        placeholder="Search technologies"
                        className={S.filterSearch}
                        aria-label="Search technologies"
                      />
                    </IconField>
                  </div>
                  <div className={S.checkboxList}>
                    {filteredTechnologies.map(tech => (
                      <div key={tech} className={S.checkboxItem}>
                        <Checkbox
                          inputId={`technology-${tech}`}
                          checked={isChecked("technologies", tech)}
                          onChange={() => handleCheckboxChange("technologies", tech)}
                        />
                        <label htmlFor={`technology-${tech}`} className={S.checkboxLabel}>
                          {getTechnologyDisplayName(tech)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cweTags.length > 0 && (
                <div className={S.filterSection}>
                  <h4 className={S.sectionTitle}>Common Weakness Enumerations (CWE)</h4>
                  <div className={S.searchInput}>
                    <IconField iconPosition="left">
                      <InputIcon className="pi pi-search" />
                      <InputText
                        value={cweSearch}
                        onChange={e => setCweSearch(e.target.value)}
                        placeholder="Search by CWE number or name"
                        className={S.filterSearch}
                        aria-label="Search CWEs"
                      />
                    </IconField>
                  </div>
                  <div className={S.checkboxList}>
                    {filteredCwe.map(cwe => (
                      <div key={cwe.value} className={S.checkboxItem}>
                        <Checkbox
                          inputId={`cwe-${cwe.value}`}
                          checked={isChecked("cwe", cwe.value)}
                          onChange={() => handleCheckboxChange("cwe", cwe.value)}
                        />
                        <label
                          htmlFor={`cwe-${cwe.value}`}
                          className={`${S.checkboxLabel} ${S.cweLabel} cwe-tooltip-target`}
                          data-pr-tooltip={
                            cwe.description ? `${cwe.name}\n\n${cwe.description}` : cwe.name
                          }
                        >
                          {cwe.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <Tooltip
                    target=".cwe-tooltip-target"
                    position="bottom"
                    className={S.cweTooltip}
                  />
                </div>
              )}

              <div className={S.filterSection}>
                <h4 className={S.sectionTitle}>Content Type</h4>
                <div className={S.checkboxList}>
                  {(LESSONS_CONTENT_TYPE_OPTIONS as { value: string; label: string }[]).map(
                    option => (
                      <div key={option.value} className={S.checkboxItem}>
                        <Checkbox
                          inputId={`contentType-${option.value}`}
                          checked={isChecked("contentType", option.value)}
                          onChange={() => handleCheckboxChange("contentType", option.value)}
                        />
                        <label htmlFor={`contentType-${option.value}`} className={S.checkboxLabel}>
                          {option.label}
                        </label>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className={S.filterSection}>
                <h4 className={S.sectionTitle}>Difficulty</h4>
                <div className={S.checkboxList}>
                  {DIFFICULTY_OPTIONS.map(option => (
                    <div key={option.value} className={S.checkboxItem}>
                      <Checkbox
                        inputId={`difficulty-${option.value}`}
                        checked={isChecked("difficulty", option.value)}
                        onChange={() => handleCheckboxChange("difficulty", option.value)}
                      />
                      <label htmlFor={`difficulty-${option.value}`} className={S.checkboxLabel}>
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className={S.filterSection}>
                <h4 className={S.sectionTitle}>Release</h4>
                <div className={S.checkboxList}>
                  {(LESSONS_RELEASE_OPTIONS as { value: string; label: string }[]).map(option => (
                    <div key={option.value} className={S.checkboxItem}>
                      <Checkbox
                        inputId={`release-${option.value}`}
                        checked={isChecked("release", option.value)}
                        onChange={() => handleCheckboxChange("release", option.value)}
                      />
                      <label htmlFor={`release-${option.value}`} className={S.checkboxLabel}>
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonFilterPanel;
